import type { ReactNode } from 'react';

export const STEPS = ['Pick', 'Processing', 'Your episode', 'People', 'Questions', 'Music & look'] as const;

/** "Step 3 of 5" with a thin progress bar; compact enough for a phone. */
export function StepHeader({ step, title, subtitle }: { step: number; title: string; subtitle?: ReactNode }) {
  return (
    <header className="mb-6">
      <p className="text-xs font-semibold tracking-wider text-white/45 uppercase">
        Step {step} of {STEPS.length} · {STEPS[step - 1]}
      </p>
      <div className="mt-2 flex gap-1" aria-hidden="true">
        {STEPS.map((s, i) => (
          <span key={s} className={`h-1 flex-1 rounded-full ${i < step ? 'bg-accent' : 'bg-white/10'}`} />
        ))}
      </div>
      <h1 className="mt-5 text-2xl font-extrabold tracking-tight sm:text-4xl">{title}</h1>
      {subtitle && <p className="mt-2 text-sm text-white/55 sm:text-base">{subtitle}</p>}
    </header>
  );
}

/** One question with single-choice chips. Tapping the chosen chip again clears it. */
export function ChipQuestion<T extends string>({
  label,
  hint,
  options,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  options: Array<{ value: T; label: string; sub?: string }>;
  value: T | null;
  onChange: (v: T | null) => void;
}) {
  return (
    <fieldset className="mb-7">
      <legend className="mb-1 text-sm font-semibold">{label}</legend>
      {hint && <p className="mb-2 text-xs text-white/45">{hint}</p>}
      <div className="mt-2 flex flex-wrap gap-2">
        {options.map((o) => {
          const on = value === o.value;
          return (
            <button
              key={o.value}
              type="button"
              aria-pressed={on}
              onClick={() => onChange(on ? null : o.value)}
              className={`focus-ring rounded-full px-4 py-2 text-sm transition ${
                on ? 'bg-white font-semibold text-black' : 'bg-white/[0.07] text-white/80 ring-1 ring-white/10 hover:bg-white/[0.12]'
              }`}
            >
              {o.label}
              {o.sub && <span className={`ml-1.5 text-xs ${on ? 'text-black/55' : 'text-white/40'}`}>{o.sub}</span>}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

/** Sticky bottom action bar: thumb-reachable on phones, inline on desktop. */
export function ActionBar({ children }: { children: ReactNode }) {
  return (
    <div className="sticky bottom-0 z-10 -mx-4 mt-8 flex gap-3 border-t border-white/5 bg-ink-950/90 px-4 py-3 backdrop-blur sm:static sm:mx-0 sm:border-0 sm:bg-transparent sm:px-0 sm:backdrop-blur-none">
      {children}
    </div>
  );
}

export const primaryBtn =
  'focus-ring flex-1 rounded-md bg-accent py-3 text-sm font-bold text-white transition hover:bg-accent-soft disabled:opacity-40 sm:flex-none sm:px-8';
export const secondaryBtn =
  'focus-ring rounded-md bg-white/[0.08] px-5 py-3 text-sm font-semibold text-white/80 ring-1 ring-white/10 transition hover:bg-white/[0.14] disabled:opacity-40';
